import React, { useState } from "react";
import {
  Typography,
  Divider,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Slider,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  TextField,
  RadioGroup,
  Radio,
} from "@mui/material";
import { useTranslation } from "react-i18next";

const FilterModal = ({ open, onClose, onApply }) => {
  const { t } = useTranslation();

  const [filters, setFilters] = useState({
    search: "",
    vehicleType: null,
    price: [50000, 1000000],
    passengers: [],
    transmission: [],
    drivetrain: [],
  });

  const handleCheckboxChange = (category, value) => {
    setFilters((prev) => {
      const current = prev[category] || [];
      if (current.includes(value)) {
        return { ...prev, [category]: current.filter((v) => v !== value) };
      }
      return { ...prev, [category]: [...current, value] };
    });
  };

  const handlePriceChange = (_, newValue) => {
    setFilters((prev) => ({ ...prev, price: newValue }));
  };

  const handleSearchChange = (e) => {
    setFilters((prev) => ({ ...prev, search: e.target.value }));
  };

  const handleVehicleTypeChange = (e) => {
    setFilters((prev) => ({ ...prev, vehicleType: e.target.value }));
  };

  const handleApply = () => {
    onApply(filters);
    onClose();
  };

  const handleClear = () => {
    const reset = {
      search: "",
      vehicleType: null,
      price: [50000, 1000000],
      passengers: [],
      transmission: [],
      drivetrain: [],
    };
    setFilters(reset);
    onApply(reset);
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        {t("filters")}
        <IconButton onClick={onClose} size="small">
          ✕
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {/* 🔍 Search Vehicle */}
        <TextField
          fullWidth
          variant="outlined"
          label={t("search_vehicle")}
          value={filters.search}
          onChange={handleSearchChange}
          sx={{ mb: 3 }}
        />

        {/* 🚗 Vehicle Type - Car / Motorcycle */}
        <Typography variant="subtitle1" sx={{ mb: 1 }}>
          {t("vehicle_type")}
        </Typography>
        <RadioGroup row value={filters.vehicleType} onChange={handleVehicleTypeChange} sx={{ mb: 2 }}>
          <FormControlLabel value={null} control={<Radio />} label={t("all")} />
          <FormControlLabel value="car" control={<Radio />} label={t("car")} />
          <FormControlLabel value="motorcycle" control={<Radio />} label={t("motorcycle")} />
        </RadioGroup>
        <Divider sx={{ my: 2 }} />

        {/* 💰 Price */}
        <Typography variant="subtitle1">{t("price_per_day")}</Typography>
        <Slider
          value={filters.price}
          onChange={handlePriceChange}
          valueLabelDisplay="auto"
          min={50000}
          max={1000000}
          step={50000}
        />
        <Divider sx={{ my: 2 }} />

        {/* 👥 Total Passenger */}
        <Typography variant="subtitle1">{t("total_passenger")}</Typography>
        <FormGroup>
          {[4, 6, 8].map((p) => (
            <FormControlLabel
              key={p}
              control={
                <Checkbox
                  checked={filters.passengers.includes(p)}
                  onChange={() => handleCheckboxChange("passengers", p)}
                />
              }
              label={`${p} ${t("passenger")}`}
            />
          ))}
        </FormGroup>
        <Divider sx={{ my: 2 }} />

        {/* ⚙️ Transmission */}
        <Typography variant="subtitle1">{t("transmission")}</Typography>
        <FormGroup>
          {["Automatic", "Manual"].map((tVal) => (
            <FormControlLabel
              key={tVal}
              control={
                <Checkbox
                  checked={filters.transmission.includes(tVal)}
                  onChange={() => handleCheckboxChange("transmission", tVal)}
                />
              }
              label={tVal}
            />
          ))}
        </FormGroup>
        <Divider sx={{ my: 2 }} />

        {/* 🛞 Drivetrain */}
        <Typography variant="subtitle1">{t("drivetrain")}</Typography>
        <FormGroup>
          {["2WD", "4WD"].map((d) => (
            <FormControlLabel
              key={d}
              control={
                <Checkbox
                  checked={filters.drivetrain.includes(d)}
                  onChange={() => handleCheckboxChange("drivetrain", d)}
                />
              }
              label={d}
            />
          ))}
        </FormGroup>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClear} color="error">
          {t("clear_all")}
        </Button>
        <Button
          onClick={handleApply}
          sx={{
            backgroundColor: "#f5272b",
            padding: "1rem 2rem",
            borderRadius: "0.3rem",
            boxShadow: "0 10px 15px 0 rgba(255, 83, 48, 0.35)",
            transition: "all 0.3s",
            border: "2px solid #f5272b",
            fontSize: "1rem",
            color: "#fff",
            "&:hover": {
              backgroundColor: "#d81e22",
              borderColor: "#d81e22",
            },
          }}
        >
          {t("apply_filters")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FilterModal;
